// <!--getrootless
// `juicy-draggable element` - Custom element to make items draggable and droppable.
// @element juicy-draggable
// version: 1.0.0
// https://github.com/Juicy/juicy-draggable/blob/master/juicy-draggable.html
// -->
// Modified by rpgcubed for Runner Idle

import {LitElement} from 'lit';
import {property} from 'lit/decorators.js';

// Okay, I've got to rewrite this whole thing or just redo it myself

const findAllDeep = (parent, selectors, depth = null) => {
  const nodes = new Set();
  const currentDepth = 1;
  const recordResult = (nodesArray) => {
    for (const node of nodesArray) {
      nodes.add(node);
    }
  };
  const recursiveSeek = (_parent) => {
    // check for selectors in lightdom
    recordResult(_parent.querySelectorAll(selectors));
    if (_parent.shadowRoot) {
      // check for selectors in shadowRoot
      recordResult(_parent.shadowRoot.querySelectorAll(selectors));
      // look for nested components with shadowRoots
      for (const child of [..._parent.shadowRoot.querySelectorAll('*')].filter(
        (i) => i.shadowRoot
      )) {
        // make sure we haven't hit our depth limit
        if (depth === null || currentDepth < depth) {
          recursiveSeek(child);
        }
      }
    }
  };
  recursiveSeek(parent);
  return nodes;
};
//https://github.com/gtramontina/draggable.js/blob/master/draggable.js
interface IDragElement extends Element {
  whenDragStarts: (event) => {};
  whenDragging: (event) => {};
  whenDragStops: (event) => {};
  whenDragHighlight: (event) => {};
}
const draggable = (function definition() {
  function addEventListener(element, eventName, handler) {
    if (element.addEventListener) {
      element.addEventListener(eventName, handler, false);
    } else if (element.attachEvent) {
      element.attachEvent('on' + eventName, handler);
    } else {
      element['on' + eventName] = handler;
    }
  }
  function removeEventListener(element, eventName, handler) {
    if (element.removeEventListener) {
      element.removeEventListener(eventName, handler, false);
    } else if (element.detachEvent) {
      element.detachEvent('on' + eventName, handler);
    } else {
      element['on' + eventName] = null;
    }
  }
  function isDisabled(element) {
    return element.hasAttribute('data-draggable-disabled');
  }
  let currentElement;
  let currentDropElement;
  let currentOptions;
  const fairlyHighZIndex = 10;

  function draggable(options): IDragElement {
    const element = options.element;
    const handle = options.handle || element;
    const position = options.position;
    const dragElement = setPositionType(
      element,
      position,
      options.dragHelperClass
    );
    let on = 0;
    let started = false;
    setDraggableListeners(dragElement);
    addEventListener(document.getRootNode(), 'mousemove', function (event) {
      if (isDisabled(element)) {
        return;
      }
      if (on != 2 || started) {
        return;
      }
      if (position == 'static') {
        copyElement(element, dragElement);
      }

      currentOptions = options;
      started = startDragging(event, dragElement);
    });
    addEventListener(handle, 'mousemove', function (event) {
      if (isDisabled(element)) {
        return;
      }
      event.preventDefault();
    });
    addEventListener(handle, 'mousedown', function () {
      if (isDisabled(element)) {
        return;
      }
      on = 1;
      setTimeout(function () {
        if (on == 1) {
          on = 2;
        }
      }, 50);
    });
    addEventListener(document, 'mouseup', function () {
      on = 0;
      started = false;
    });
    return dragElement;
  }
  function setPositionType(element, position, dragHelperClass) {
    if (position == 'static') {
      const div = document.createElement('div');
      div.className = dragHelperClass;
      div.style.position = 'fixed';
      element.parentNode.insertBefore(div, element);
      return div;
    }
    if (!position) {
      position = 'absolute';
    }
    element.style.position = position;
    return element;
  }
  function setDraggableListeners(element) {
    element.draggableListeners = {
      start: [],
      drag: [],
      stop: [],
      highlight: [],
    };
    element.whenDragStarts = addListener(element, 'start');
    element.whenDragging = addListener(element, 'drag');
    element.whenDragStops = addListener(element, 'stop');
    element.whenDragHighlight = addListener(element, 'highlight');
  }
  function copyElement(from, to) {
    const rec = from.getBoundingClientRect();
    to.innerHTML = from.innerHTML;
    to.style.width = inPixels(from.offsetWidth);
    to.style.height = inPixels(from.offsetHeight);
    to.style.left = inPixels(rec.left);
    to.style.top = inPixels(rec.top);
  }
  function startDragging(event, element) {
    clearSelection();
    currentElement && sendToBack(currentElement);
    currentElement = bringToFront(element);
    currentElement.classList.add(currentOptions.draggingClass);
    const initialPosition = getInitialPosition(currentElement);
    currentElement.style.left = inPixels(initialPosition.left);
    currentElement.style.top = inPixels(initialPosition.top);
    currentElement.lastXPosition = event.clientX;
    currentElement.lastYPosition = event.clientY;
    const okToGoOn = triggerEvent('start', {
      x: initialPosition.left,
      y: initialPosition.top,
      mouseEvent: event,
    });

    if (!okToGoOn) {
      currentElement.classList.remove(currentOptions.draggingClass);
      return false;
    }

    addDocumentListeners();

    return true;
  }
  function stopDragging(event) {
    removeDocumentListeners();
    const left = parseInt(currentElement.style.left, 10);
    const top = parseInt(currentElement.style.top, 10);
    if (currentDropElement) {
      currentDropElement.classList.remove(currentOptions.dragHoverClass);
    }
    triggerEvent('stop', {
      x: left,
      y: top,
      mouseEvent: event,
      dropElement: currentDropElement,
      dragElement: currentElement,
    });
    currentElement.classList.remove(currentOptions.draggingClass);
  }
  function addListener(element, type) {
    return function (listener) {
      element.draggableListeners[type].push(listener);
    };
  }
  function triggerEvent(type, args) {
    let result = true;
    const listeners = currentElement.draggableListeners[type];
    for (let i = listeners.length - 1; i >= 0; i--) {
      if (listeners[i](args) === false) result = false;
    }
    return result;
  }
  function sendToBack(element) {
    const decreasedZIndex = fairlyHighZIndex - 1;
    element.style['z-index'] = decreasedZIndex;
    element.style['zIndex'] = decreasedZIndex;
  }
  function bringToFront(element) {
    element.style['z-index'] = fairlyHighZIndex;
    element.style['zIndex'] = fairlyHighZIndex;
    return element;
  }
  function addDocumentListeners() {
    addEventListener(document, 'selectstart', cancelDocumentSelection);
    addEventListener(document, 'mousemove', repositionElement);
    addEventListener(document, 'mouseup', stopDragging);
  }
  function getInitialPosition(element) {
    const rec = element.getBoundingClientRect();
    const style = element.currentStyle || window.getComputedStyle(element);
    return {
      top: rec.top - parseInt(style.marginTop),
      left: rec.left - parseInt(style.marginLeft),
    };
  }
  function inPixels(value) {
    return value + 'px';
  }
  function cancelDocumentSelection(event) {
    event.preventDefault && event.preventDefault();
    event.stopPropagation && event.stopPropagation();
    event.returnValue = false;
    return false;
  }
  function repositionElement(event) {
    event.preventDefault && event.preventDefault();
    event.returnValue = false;
    const style = currentElement.style;
    const elementXPosition = parseInt(style.left, 10);
    const elementYPosition = parseInt(style.top, 10);
    const elementNewXPosition =
      elementXPosition + (event.clientX - currentElement.lastXPosition);
    const elementNewYPosition =
      elementYPosition + (event.clientY - currentElement.lastYPosition);
    style.left = inPixels(elementNewXPosition);
    style.top = inPixels(elementNewYPosition);
    style.right = 'auto';
    style.bottom = 'auto';
    currentElement.lastXPosition = event.clientX;
    currentElement.lastYPosition = event.clientY;
    highlightDropElement(event);
    triggerEvent('drag', {
      x: elementNewXPosition,
      y: elementNewYPosition,
      mouseEvent: event,
    });
  }
  function highlightDropElement(event) {
    currentDropElement = null;
    if (!currentOptions.dropSelector) {
      return null;
    }
    const items = Array.from(
      findAllDeep(
        document.querySelector('runner-idle'),
        currentOptions.dropSelector
      )
    ) as HTMLElement[];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const rec = item.getBoundingClientRect();
      const x = event.clientX;
      const y = event.clientY;
      if (x >= rec.left && x <= rec.right && y >= rec.top && y <= rec.bottom) {
        currentDropElement = item;
        item.classList.add(currentOptions.dragHoverClass);
        triggerEvent('highlight', {x: x, y: y, item: item, mouseEvent: event});
      } else {
        item.classList.remove(currentOptions.dragHoverClass);
      }
    }
  }
  function removeDocumentListeners() {
    removeEventListener(document, 'selectstart', cancelDocumentSelection);
    removeEventListener(document, 'mousemove', repositionElement);
    removeEventListener(document, 'mouseup', stopDragging);
  }
  function clearSelection() {
    window.getSelection()?.removeAllRanges();
  }
  return draggable;
})();

class JuicyDraggable extends LitElement {
  @property() position;
  @property() dropSelector;
  element: Element;
  dragHelperClass = '';
  dragHoverClass = '';
  draggingClass = '';
  elementId = '';
  handleId = '';
  constructor() {
    super();
    this.element = document.createElement('div');
  }

  static override get observedAttributes() {
    return ['disabled'];
  }

  override attributeChangedCallback(attributeName) {
    let value;
    switch (attributeName) {
      case 'disabled':
        value = this.hasAttribute('disabled');
        if (value) {
          this.element?.setAttribute('data-draggable-disabled', 'disabled');
        } else {
          this.element?.removeAttribute('data-draggable-disabled');
        }
        break;
    }
  }

  override connectedCallback() {
    let element: IDragElement | null = null;
    let handle: IDragElement | null = null;
    if (this.shadowRoot) return;
    const root = this.attachShadow({mode: 'open'});
    root.innerHTML = '<slot id="content"></slot>';
    this.refreshAttributeValues();
    if (!this.elementId) {
      element = this.children[0] as IDragElement;
    } else {
      element = this.querySelector('#' + this.elementId);
    }
    if (this.handleId) {
      handle = this.querySelector('#' + this.handleId);
    }
    this.dragHelperClass = this.dragHelperClass || 'drag-helper';
    this.dragHoverClass = this.dragHoverClass || 'drag-hover';
    this.draggingClass = this.draggingClass || 'dragging';
    if (element) this.element = element;
    element = draggable({
      element: element,
      handle: handle,
      position: this.position,
      dropSelector: this.dropSelector,
      dragHelperClass: this.dragHelperClass,
      dragHoverClass: this.dragHoverClass,
      draggingClass: this.draggingClass,
    });
    element.whenDragStarts((e) => {
      const event = new CustomEvent('juicy-draggable-start', {detail: e});
      this.dispatchEvent(event);
      return !(event.detail.cancel === true);
    });
    element.whenDragging((e) => {
      const event = new CustomEvent('juicy-draggable-move', {detail: e});
      this.dispatchEvent(event);
    });
    element.whenDragStops((e) => {
      const event = new CustomEvent('juicy-draggable-stop', {detail: e});
      this.dispatchEvent(event);
    });
    element.whenDragHighlight((e) => {
      const event = new CustomEvent('juicy-draggable-highlight', {detail: e});
      this.dispatchEvent(event);
    });
  }

  getScreenSize() {
    const w = window;
    const d = document;
    const e = d.documentElement;
    const g = d.getElementsByTagName('body')[0];
    const x = w.innerWidth || e.clientWidth || g.clientWidth;
    const y = w.innerHeight || e.clientHeight || g.clientHeight;
    return {x: x, y: y};
  }
  refreshAttributeValues() {
    const attributes = [
      'elementId',
      'handleId',
      'position',
      'dropSelector',
      'dragHelperClass',
      'dragHoverClass',
      'draggingClass',
      'disabled',
    ];
    for (let i = 0; i < attributes.length; i++) {
      const a = attributes[i];
      if (this.hasAttribute(a)) {
        this[a] = this.attributes[a].nodeValue;
        this.attributeChangedCallback(a);
      }
    }
  }
}
//Throws a weird exception in IE;
//JuicyDraggable.disabled = false;

customElements.define('juicy-draggable', JuicyDraggable);
